import { useState } from "react";
import { useEffect } from "react";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import InputBox from "./components/InputBox";
import Results from "./components/Results";
import UploadDocs from "./components/UploadDocs"; // ✅ already imported
import { generateTestCases, getHistory } from "./services/api";
import ProjectSelector from "./components/ProjectSelector";
import AgentSteps from "./components/AgentSteps";
import { generateWithAgentV2 } from "./services/api";
import Admin from "./pages/Admin";

function App() {
  // 🔐 Auth State
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [page, setPage] = useState("login"); // login | signup
  // 📁 Project State (ADD HERE)
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [activeTab, setActiveTab] = useState("generate");
  const [steps, setSteps] = useState([]);
  const [mode, setMode] = useState("normal"); // normal | agent
  // 🧠 App State (UNCHANGED)
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
  setHistory([]);
  setData(null);
  }, [projectId]);

  // 🔹 UPDATED: now accepts useContext
  const handleGenerate = async (requirement, useContext) => {
  if (!requirement) {
    setError("Please enter requirement");
    return;
  }

  if (!projectId) {
    setError("Please select a project");
    return;
  }

  setLoading(true);
  setError("");
  setData(null);
  setSteps([]);

  try {
    let res;

    if (mode === "agent") {
      res = await generateWithAgentV2({
        requirement,
        project_id: projectId,
      });

      setSteps(res.data.steps || []);
    } else {
      res = await generateTestCases({
        requirement,
        use_context: useContext,
        project_id: projectId,
      });
    }

    if (res.data.error) {
      setError(res.data.error);
    } else {
      setData(res.data);
    }

  } catch (err) {
    setError("Failed to connect to backend");
  }

  setLoading(false);
  };

  // 🔹 Load History
  const handleLoadHistory = async () => {
    if (!projectId) {
    setError("Please select a project");
    return;
    }
    setHistoryLoading(true);

    try {
      const res = await getHistory(projectId);
      setHistory(res.data);
    } catch (err) {
      setError("Failed to load history");
    }

    setHistoryLoading(false);
  };
  // 🔓 Logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setPage("login");
  };

  // =============================
  // 🔐 AUTH SCREENS
  // =============================

  if (!token) {

    if (page === "signup") {
      return (
        <Signup goToLogin={() => setPage("login")} />
      );
    }

    return (
      <Login
        setToken={setToken}
        goToSignup={() => setPage("signup")}
      />
    );
  }

  // =============================
  // 🚀 MAIN APP (YOUR EXISTING UI)
  // =============================

return (
  <div className="flex min-h-screen bg-gray-50">

    {/* 🔵 Sidebar */}
    <div className="w-60 bg-gradient-to-b from-indigo-600 to-blue-500 text-white p-4 space-y-4 shadow-lg">

      <h1 className="text-xl font-bold mb-6">QA Copilot</h1>

      <button onClick={() => setActiveTab("generate")} className=
      {`block w-full text-left px-3 py-2 rounded ${
    activeTab === "generate"
      ? "bg-white text-indigo-600 font-semibold"
      : "hover:bg-indigo-400"
      }`}>
        Generate Tests
      </button>

      <button onClick={() => setActiveTab("history")} className=
      {`block w-full text-left px-3 py-2 rounded ${
    activeTab === "history"
      ? "bg-white text-indigo-600 font-semibold"
      : "hover:bg-indigo-400"
  }`}>
        History
      </button>

      <button onClick={() => setActiveTab("upload")} className=
      {`block w-full text-left px-3 py-2 rounded ${
    activeTab === "upload"
      ? "bg-white text-indigo-600 font-semibold"
      : "hover:bg-indigo-400"
  }`}>
        Upload Docs
      </button>

      <button
        onClick={() => setActiveTab("admin")}
        className={`block w-full text-left px-3 py-2 rounded ${
          activeTab === "admin"
            ? "bg-white text-indigo-600 font-semibold"
            : "hover:bg-indigo-400"
        }`}
      >
        Admin
      </button>

      <button
        onClick={handleLogout}
        className="bg-red-500 px-3 py-1 rounded mt-6"
      >
        Logout
      </button>
    </div>

    {/* 🟢 Main Content */}
    <div className="flex-1 p-8 bg-gray-100">

      <h1 className="text-3xl font-bold mb-6 text-indigo-700">
        QA AI Copilot 🚀
      </h1>

      {/* ✅ Project Selector ALWAYS visible */}
      <ProjectSelector
        projectId={projectId}
        setProjectId={setProjectId}
        setProjectName={setProjectName}
      />

            {/* 🔥 Mode Toggle */}
      <div className="flex justify-center gap-4 mb-4">

        <button
          onClick={() => setMode("normal")}
          className={`px-4 py-2 rounded ${
            mode === "normal"
              ? "bg-blue-600 text-white"
              : "bg-gray-200"
          }`}
        >
          Normal Mode
        </button>

        <button
          onClick={() => setMode("agent")}
          className={`px-4 py-2 rounded ${
            mode === "agent"
              ? "bg-purple-600 text-white"
              : "bg-gray-200"
          }`}
        >
          AI Agent Mode 🤖
        </button>

      </div>

      {/* Active Project */}
      {projectName && (
      <div className="inline-block bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full mb-4 text-sm">
        Active Project: {projectName}
      </div>
      )}

      {/* ===================== */}
      {/* 🔀 GENERATE TAB */}
      {/* ===================== */}
      {activeTab === "generate" && (
        <>
          <InputBox onGenerate={handleGenerate} loading={loading}/>
          {/* 🤖 Agent Thinking */}
          {mode === "agent" && <AgentSteps steps={steps} />}
          
          {loading && (
            <p className={`text-center mt-4 ${
              mode === "agent" ? "text-purple-600" : ""
            }`}>
              {mode === "agent"
                ? "🤖 Agent is thinking..."
                : "Generating..."}
            </p>
          )}
          {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mt-4">
          {error}
          </div>
          )}

          <Results data={data} />
        </>
      )}

      {/* ===================== */}
      {/* 📜 HISTORY TAB */}
      {/* ===================== */}
      {activeTab === "history" && (
        <>
          <button
            onClick={handleLoadHistory}
            className="bg-green-500 text-white px-4 py-2 rounded mb-4"
          >
            Load History
          </button>

          {historyLoading && <p>Loading...</p>}

          {history.map((item) => (
            <div key={item.id} className="mb-4 p-4 bg-white rounded shadow">
              <p className="font-semibold">{item.requirement}</p>
              <Results data={item} />
            </div>
          ))}
        </>
      )}

      {/* ===================== */}
      {/* 📂 UPLOAD TAB */}
      {/* ===================== */}
      {activeTab === "upload" && (
        <UploadDocs projectId={projectId} />
      )}
      {activeTab === "admin" && (
        <Admin />
      )}

    </div>
  </div>
);
}

export default App;